import React, { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { FormMessage } from '../../types';
import { buildAnnualDelay, getDelayDays, getDelayTime, isDelayBefore } from '../../helpers/delayHelpers';
import { CONTROL_HEIGHT } from './controlSizing';

/** 9:00, the time an annual message goes out unless the user picks another. */
const DEFAULT_MINUTES = 540;
const MINUTES_PER_HOUR = 60;

interface AnnualScheduleRowProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  disabled?: boolean;
}

/** The inline captions this row sets between its controls. */
const Caption: React.FC<{ children: React.ReactNode; marginLeft?: string }> = ({
  children, marginLeft,
}) => (
  <FormControl style={{ flexDirection: 'row', alignItems: 'center', marginLeft }}>
    <Typography component="label" color="text.primary">{children}</Typography>
  </FormControl>
);

/**
 * "Schedule: [n] Days [Before|After] at [time]".
 *
 * Annual messages are anchored to a contact date rather than to a preceding
 * message, so this is a widget of its own rather than a variant of ScheduleRow:
 * there is no schedule-type dropdown, and the three controls encode one delay
 * string between them.
 */
export const AnnualScheduleRow: React.FC<AnnualScheduleRowProps> = ({
  message, onChange, disabled,
}) => {
  const [pastOrFuture, setPastOrFuture] = useState<string>(() => {
    // "On the day" reads as Before, so the dropdown has a value to show.
    if (!message.delay || getDelayDays(message.delay) === 0) return 'before';
    return isDelayBefore(message.delay) ? 'before' : 'after';
  });
  const [delayDays, setDelayDays] = useState<number | undefined>(
    message.delay ? getDelayDays(message.delay) : 0,
  );
  const [delayTime, setDelayTime] = useState<number>(
    message.delay ? getDelayTime(message.delay) : DEFAULT_MINUTES,
  );

  /** The three controls always write the whole delay, never a part of it. */
  const write = (direction: string, days: number, minutes: number) =>
    onChange({ time: null, delay: buildAnnualDelay(direction, days, minutes) });

  const handleDaysChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setDelayDays(value === '' ? undefined : Number(value));
    // An empty field is not zero days — it is an unusable delay, and 'NaN' is
    // what the primary-action check looks for.
    if (!value) onChange({ time: null, delay: 'NaN' });
    else write(pastOrFuture, Number(value), delayTime);
  };

  const handleDirectionChange = ({ target }: SelectChangeEvent<string>) => {
    setPastOrFuture(target.value);
    write(target.value, delayDays ?? 0, delayTime);
  };

  const handleTimeChange = (time: Dayjs | null) => {
    if (!time) return;
    const minutes = time.get('h') * MINUTES_PER_HOUR + time.get('m');
    setDelayTime(minutes);
    write(pastOrFuture, delayDays ?? 0, minutes);
  };

  const daysValue = (delayDays === 0 || delayDays) && !Number.isNaN(delayDays)
    ? Math.abs(delayDays)
    : '';

  return (
    <>
      <Divider sx={{ marginTop: '20px' }} />
      <Stack direction="row" alignItems="center" height="30px" sx={{ marginTop: '10px', marginBottom: '10px' }}>
        {/* The controls encode a delay, so they only make sense on one. The row
            itself still draws, as it always has. */}
        {message.scheduledType === 'delay' && (
        <Box display="flex" sx={{ flexDirection: 'column', marginTop: '13px' }}>
          <Box display="flex">
            <Caption>Schedule:</Caption>
            <TextField
              disabled={disabled}
              sx={{
                width: '90px',
                marginRight: '10px',
                marginLeft: '10px',
                '& .MuiOutlinedInput-input': {
                  height: CONTROL_HEIGHT, paddingTop: 0, paddingBottom: 0,
                },
              }}
              placeholder="Period"
              name="delay"
              type="number"
              inputProps={{ min: 0 }}
              variant="outlined"
              value={daysValue}
              onChange={handleDaysChange}
            />
            <Caption>Days</Caption>
            <Select
              disabled={disabled}
              sx={{ height: CONTROL_HEIGHT, marginLeft: '10px' }}
              MenuProps={{ disableScrollLock: true }}
              size="small"
              // Zero days is "on the day", which has no direction to pick.
              value={Math.abs(delayDays ?? 0) === 0 ? 'before' : pastOrFuture}
              onChange={handleDirectionChange}
              name="annualDirection"
            >
              <MenuItem value="before">Before</MenuItem>
              <MenuItem value="after">After</MenuItem>
            </Select>
            <Caption marginLeft="10px">at</Caption>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* 'DateTimePicker', not 'TimePicker': DemoContainer keys the
                  min-width it puts on its child TextField off this name — 270px
                  for a date-time child, 200px for a time-only one. The control is
                  a TimePicker either way; this name is what sizes the row. */}
              <DemoContainer sx={{ marginTop: '-8px' }} components={['DateTimePicker']}>
                <TimePicker
                  disabled={disabled}
                  sx={{
                    '& .MuiStack-root': { paddingTop: 0 },
                    '& .MuiOutlinedInput-root': {
                      height: CONTROL_HEIGHT, width: '150px', marginRight: 0, marginLeft: '10px',
                    },
                    overflow: 'hidden',
                  }}
                  ampm
                  format="hh:mm A"
                  onChange={handleTimeChange}
                  defaultValue={dayjs().startOf('d').add(Math.abs(delayTime) || DEFAULT_MINUTES, 'm')}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Box>
        </Box>
        )}
      </Stack>
    </>
  );
};
